import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { Permission } from '@Constants/Permission.g';
import { vehicleColumns, vehicleTypeColumns } from '@Constants/TableColumns';
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { useHasPermission } from '@Contexts/appContext';
import type { ApiVehicle, ApiVehicleType } from '@Model';
import { FrameEditVehicleType } from '@Modules/Stammdaten';
import {
    Card,
    Checkbox,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedFieldInFrame,
    useTranslate,
    NumberInput,
    DateInput,
} from '@synqo/frontend-component-library';
import { EnumFrontendLayoutSetting } from '@Model';
import { useParams } from 'react-router';

export function FrameEditVehicle(props: IFrameEditShellProps<ApiVehicle>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiVehicle>
            apiResourcePath={`${STAMMDATEN_API}/vehicles`}
            renderer={FrameEditVehicleBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.StammdatenVehiclesUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.StammdatenVehiclesCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.StammdatenVehiclesDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditVehicleBody(props: IFrameBodyProps<ApiVehicle>) {
    const i18n = useTranslate('');

    const apiVehicle = props.data;

    const binder = useGuiBinder<ApiVehicle>({
        ...props,
        canEdit: props.mode !== EditFrameMode.View,
    });

    const linkTrailer = useLinkedFieldInFrame(props, 'trailer', FrameEditVehicle);
    const linkType = useLinkedFieldInFrame(props, 'type', FrameEditVehicleType);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Fahrzeug', props.mode),
                resourceLabel: 'Fahrzeug',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="PrimaryVehicleData" border={'withoutBorder'}>
                <Grid columns={12}>
                    <GridColumns spans={[3, 3, 3, 3, 12]}>
                        <TextInput {...binder.bindToProp<string>('licence_plate')} label={i18n('Kennzeichen')} />
                        <TextInput {...binder.bindToProp<string>('number')} label={i18n('Nummer')} />
                        <TextInput {...binder.bindToProp<string>('label')} label={i18n('Bezeichnung')} />
                        <TextInput {...binder.bindToProp<string>('shorthand')} label={i18n('Suchcode')} />
                        <TextInput {...binder.bindToProp<string>('remarks')} inputSize={3} label={i18n('Bemerkung')} />
                    </GridColumns>
                </Grid>
            </Card>
            <Card name="VehicleType" label="Fahrzeugtyp-Informationen">
                <LinkedFieldWithLayoutSettings<ApiVehicleType>
                    layoutSettingsContext={EnumFrontendLayoutSetting.VehicleTypesTableLayout}
                    {...binder.bindToLinkProperty(linkType.linkApi)}
                    {...linkType.events}
                    columns={vehicleTypeColumns}
                    label={i18n('Fahrzeugtyp')}
                />
                <Checkbox {...binder.bindToAutoConfirmed<boolean>('is_trailer')} label={i18n('Anhänger')} />
                {!apiVehicle.is_trailer && (
                    <LinkedFieldWithLayoutSettings<ApiVehicle>
                        layoutSettingsContext={EnumFrontendLayoutSetting.VehiclesTableLayout}
                        {...binder.bindToLinkProperty(linkTrailer.linkApi)}
                        {...linkTrailer.events}
                        columns={vehicleColumns}
                        label={i18n('Anhänger')}
                    />
                )}
            </Card>
            <Card name="VehicleInfo" label="Informationen aus den Fahrzeugpapieren">
                <Grid>
                    <GridColumns spans={[6, 6]}>
                        <TextInput {...binder.bindToProp<string>('manufacturer')} label={i18n('Hersteller')} />
                        <NumberInput {...binder.bindToProp<number>('construction_year')} label={i18n('Baujahr')} />
                    </GridColumns>
                </Grid>
            </Card>
            <Card name="VehicleWeights" label="Fahrzeug-Gewichte">
                <Grid>
                    <GridColumns spans={[4, 4, 4]}>
                        <NumberInput {...binder.bindToProp<number>('current_weight')} label={i18n('Leergewicht')} />
                        <DateInput
                            type="day"
                            {...binder.bindToProp<string>('current_weight_valid_until')}
                            label={i18n('Leergewicht gültig bis')}
                        />
                        <NumberInput
                            {...binder.bindToProp<number>('gross_vehicle_weight_rating')}
                            label={i18n('Zulässiges Gesamtgewicht')}
                        />
                    </GridColumns>
                </Grid>
            </Card>
        </EditFrameShell>
    );
}
